import { Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs';

/**
 * Service to handle global coupon override functionality from Banner section in Craft.
 * When an override coupon is set, it will be collected by the coupon aggregation system
 * and included in the hc parameter for site-wide sales.
 */
@Injectable({
  providedIn: 'root',
})
export class GlobalCouponOverrideService {
  private overrideCoupon$ = new BehaviorSubject<string | null>(null);

  /**
   * Observable that emits the current override coupon code.
   * @deprecated Banner coupons are now read directly from route data. This is kept for backwards compatibility.
   */
  get overrideCouponCode(): Observable<string | null> {
    return this.overrideCoupon$.asObservable();
  }

  /**
   * Sets the global override coupon code from the Banner section.
   * @param couponCode - The coupon code to use for site-wide override, or null to clear
   * @deprecated Banner coupons are now read directly from route data. This is kept for backwards compatibility.
   */
  setOverrideCoupon(couponCode: string | null): void {
    this.overrideCoupon$.next(couponCode);
  }

  /**
   * Gets the current override coupon code synchronously.
   * @returns The current override coupon code or null if not set
   */
  getCurrentOverrideCoupon(): string | null {
    return this.overrideCoupon$.value;
  }

  /**
   * Legacy method that no longer modifies URLs directly.
   * Override coupons are now handled by the coupon aggregation system.
   * @param url - The original checkout URL
   * @returns An object containing the unmodified URL and wasModified: false
   * @deprecated Use CheckoutUrlBuilderService.buildCheckoutUrlWithAllCoupons for proper coupon handling
   */
  appendOverrideCouponToUrl(url: string): { url: string; wasModified: boolean } {
    return { url, wasModified: false };
  }

  /**
   * Clears the override coupon.
   */
  clearOverrideCoupon(): void {
    this.setOverrideCoupon(null);
  }
}
